import { useRef } from "react";
import { toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import { setValidationLoading } from "redux/CreateDataset/actions";

export interface UseDragDropProps {
  onFilesChange: (files: File[]) => void;
  dispatch?: (action: any) => void; // Add dispatch for setting validation state
}

type DirEntry = FileSystemDirectoryEntry | any; // DOM types vary across environments

export const useDragDrop = ({ onFilesChange, dispatch }: UseDragDropProps) => {
  const isDragDropProcessing = useRef<boolean>(false);
  const toastIdRef = useRef<any>(null);

  const createFileWithPath = (file: File, relativePath: string): File => {
    const newFile = new File([file], relativePath, { type: file.type });
    Object.defineProperty(newFile, "webkitRelativePath", {
      value: relativePath,
      writable: false,
      enumerable: true,
      configurable: false,
    });
    return newFile;
  };

  // Recursively read a directory entry and return Promise<File[]>
  const readDirectory = (entry: DirEntry, path = ""): Promise<File[]> => {
    return new Promise((resolve, reject) => {
      const files: File[] = [];
      const reader = entry.createReader();

      const readBatch = () => {
        reader.readEntries(async (entries: any[]) => {
          if (!entries || entries.length === 0) {
            resolve(files);
            return;
          }

          try {
            // process entries in this batch sequentially so subdirectories are awaited
            for (const ent of entries) {
              if (ent.isFile) {
                const file: File = await new Promise((res, rej) => {
                  (ent as FileSystemFileEntry).file(res, rej);
                });

                const relativePath = path ? `${path}/${ent.name}` : ent.name;
                files.push(createFileWithPath(file, relativePath));
              } else if (ent.isDirectory) {
                const subPath = path ? `${path}/${ent.name}` : ent.name;
                const subFiles = await readDirectory(ent as DirEntry, subPath);
                files.push(...subFiles);
              }
            }
            // read next batch
            readBatch();
          } catch (err) {
            reject(err);
          }
        }, reject);
      };

      readBatch();
    });
  };

  const showFileOnlyError = () => {
    toastIdRef.current = toast(
      <VLToastComponent
        title="Invalid upload"
        status="ERROR"
        description="Please drop folders only. Individual files are not allowed."
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
      { autoClose: 7000, closeButton: false, closeOnClick: false },
    );
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDragDropProcessing.current = true;

    // Set validation loading state if dispatch is available
    if (dispatch) {
      dispatch(setValidationLoading(true));
    }

    const items = Array.from(e.dataTransfer.items || []);
    const filesAccumulator: File[] = [];
    let hasFolders = false;
    let hasIndividualFiles = false;

    // If browser supports webkitGetAsEntry, use it; otherwise fallback
    const tasks = items.map(async (item: DataTransferItem) => {
      // Prefer webkitGetAsEntry for directories
      const entry = (item as any).webkitGetAsEntry?.();

      if (entry) {
        if (entry.isDirectory) {
          hasFolders = true;
          const folderFiles = await readDirectory(
            entry as DirEntry,
            entry.name,
          );
          filesAccumulator.push(...folderFiles);
        } else if (entry.isFile) {
          // entry is a file entry - this is an individual file, not part of a folder
          hasIndividualFiles = true;
          const file: File = await new Promise((res, rej) =>
            (entry as FileSystemFileEntry).file(res, rej),
          );
          const rel = (entry.fullPath || entry.name).replace(/^\//, "");
          filesAccumulator.push(createFileWithPath(file, rel));
        }
      } else {
        // fallback (some browsers don't return entries for directories)
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            // If file has a webkitRelativePath (when folder is dropped via input[type=file] with webkitdirectory),
            // preserve it; otherwise use file.name
            const rel = (file as any).webkitRelativePath || file.name;

            // Check if this is an individual file (no webkitRelativePath or no folder structure)
            if (!(file as any).webkitRelativePath || !rel.includes("/")) {
              hasIndividualFiles = true;
            }

            filesAccumulator.push(createFileWithPath(file, rel));
          }
        }
      }
    });

    // wait for all items to be processed
    await Promise.all(tasks);

    // Final fallback: if nothing came from items, try dataTransfer.files (some environments only populate this)
    if (
      filesAccumulator.length === 0 &&
      e.dataTransfer.files &&
      e.dataTransfer.files.length
    ) {
      const filesList = Array.from(e.dataTransfer.files);
      for (const f of filesList) {
        const rel = (f as any).webkitRelativePath || f.name;

        // Check if this is an individual file (no webkitRelativePath or no folder structure)
        if (!(f as any).webkitRelativePath || !rel.includes("/")) {
          hasIndividualFiles = true;
        }

        filesAccumulator.push(createFileWithPath(f, rel));
      }
    }

    // Validate: Only allow folder drops, not individual files or mixed drops
    if (filesAccumulator.length > 0 && (!hasFolders || hasIndividualFiles)) {
      showFileOnlyError();
      if (dispatch) {
        dispatch(setValidationLoading(false));
      }
      setTimeout(() => {
        isDragDropProcessing.current = false;
      }, 500);
      return;
    }

    onFilesChange(filesAccumulator);

    setTimeout(() => {
      isDragDropProcessing.current = false;
    }, 500);
  };

  return { handleDrop, isDragDropProcessing };
};
